import {
  apiSuccessSchema,
  tiktokAdvertiserAccountsDataSchema,
  tiktokAdvertiserAccountsQuerySchema,
  tiktokBusinessCentersDataSchema,
  tiktokCampaignDetailDataSchema,
  tiktokCampaignDetailQuerySchema,
  tiktokCampaignsDataSchema,
  tiktokCampaignsBodySchema,
  tiktokAuthorizationStartDataSchema,
  tiktokIntegrationOverviewDataSchema,
  tiktokCreativeAssetsBodySchema,
  tiktokCreativeAssetsDataSchema,
  tiktokStoresDataSchema,
  tiktokStoresQuerySchema,
} from "@metaadsmanager/tk-master-contracts";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { apiClient } from "../api/browser-client";
import { beginTikTokOAuthFlow } from "../tiktok/oauth-return-session";
import { normalizeCreativeAssetsSuccessPayload } from "../tiktok/normalize-creative-asset-urls";
import { keepPreviousDataForWorkspace } from "./keep-previous-data-for-workspace";

const workspacePath = (workspaceId: string) =>
  `/v1/workspaces/${encodeURIComponent(workspaceId)}`;

/** 后端偶发空串/相对 URL；归一后再走契约，避免 ZodURL TypeError 打爆列表。 */
const tiktokCreativeAssetsSuccessSchema = z.preprocess(
  normalizeCreativeAssetsSuccessPayload,
  apiSuccessSchema(tiktokCreativeAssetsDataSchema),
);

const tiktokDisconnectDataSchema = z
  .object({
    status: z.literal("disconnected"),
  })
  .strict();

/** Workspace 的 TikTok OAuth 连接状态。不转发 AbortSignal：见 app-query-client。 */
export const tiktokConnectionQueryOptions = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspace", workspaceId, "tiktok", "connection"],
    refetchOnMount: "always",
    queryFn: async () => {
      const overview = await apiClient.get(
        `${workspacePath(workspaceId)}/integrations/tiktok`,
        apiSuccessSchema(tiktokIntegrationOverviewDataSchema),
      );
      return {
        businessCentersSync: overview.data.businessCentersSync,
        data: overview.data.connection,
        requestId: overview.requestId,
      };
    },
  });

/** 通过 FC Cookie 会话创建 TikTok OAuth 授权并返回跳转地址。 */
export function startTikTokAuthorization(workspaceId: string) {
  beginTikTokOAuthFlow(workspaceId);
  return apiClient.post(
    `${workspacePath(workspaceId)}/integrations/tiktok/authorize`,
    {},
    apiSuccessSchema(tiktokAuthorizationStartDataSchema),
  );
}

/** 解除当前 Workspace 的 TikTok for Business 授权连接。 */
export function disconnectTikTokAuthorization(workspaceId: string) {
  return apiClient.post(
    `${workspacePath(workspaceId)}/integrations/tiktok/disconnect`,
    {},
    apiSuccessSchema(tiktokDisconnectDataSchema),
  );
}

/** Workspace 的 TikTok Business Center 快照。 */
export const tiktokBusinessCentersQueryOptions = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspace", workspaceId, "tiktok", "business-centers"],
    refetchOnMount: "always",
    queryFn: () =>
      apiClient.get(
        `${workspacePath(workspaceId)}/tiktok/business-centers`,
        apiSuccessSchema(tiktokBusinessCentersDataSchema),
      ),
  });

/** Workspace 广告账户快照；省略 bcId 时读取整个 Workspace。 */
export const tiktokAdvertiserAccountsQueryOptions = (
  workspaceId: string,
  bcId?: string,
  options?: { campaignCount?: true },
) =>
  queryOptions({
    placeholderData: keepPreviousDataForWorkspace(workspaceId),
    queryKey: [
      "workspace",
      workspaceId,
      "tiktok",
      "advertiser-accounts",
      bcId ?? "all",
      options?.campaignCount ? "with-campaign-count" : "no-campaign-count",
    ],
    queryFn: () => {
      const query = tiktokAdvertiserAccountsQuerySchema.parse({
        ...(bcId ? { bcId } : {}),
        ...(options?.campaignCount ? { campaignCount: "true" as const } : {}),
      });
      const params = new URLSearchParams();
      if (query.bcId) params.set("bcId", query.bcId);
      if (query.campaignCount) params.set("campaignCount", query.campaignCount);
      const qs = params.toString();
      return apiClient.get(
        `${workspacePath(workspaceId)}/tiktok/advertiser-accounts${qs ? `?${qs}` : ""}`,
        apiSuccessSchema(tiktokAdvertiserAccountsDataSchema),
      );
    },
  });

/** Workspace 店铺快照；省略 bcId/advertiserId 时读取整个 Workspace（advertiser 需带 BC）。 */
export const tiktokStoresQueryOptions = (
  workspaceId: string,
  bcId?: string,
  advertiserId?: string,
) =>
  queryOptions({
    placeholderData: keepPreviousDataForWorkspace(workspaceId),
    queryKey: [
      "workspace",
      workspaceId,
      "tiktok",
      "stores",
      bcId ?? "all",
      advertiserId ?? "all",
    ],
    queryFn: () => {
      const query = tiktokStoresQuerySchema.parse({
        ...(advertiserId ? { advertiserId } : {}),
        ...(bcId ? { bcId } : {}),
      });
      const params = new URLSearchParams();
      if (query.bcId) params.set("bcId", query.bcId);
      if (query.advertiserId) params.set("advertiserId", query.advertiserId);
      const qs = params.toString();
      return apiClient.get(
        `${workspacePath(workspaceId)}/tiktok/stores${qs ? `?${qs}` : ""}`,
        apiSuccessSchema(tiktokStoresDataSchema),
      );
    },
  });

/**
 * Workspace 内 Product GMV Max 创意分析（ADR-0080：服务端分页 + 高级筛选）。
 * bcId/advertiserId 可选（全部模式）；page/pageSize/sort/direction/q/status/authorization/af
 * 全部透传后端。rawQuery 整体作 queryKey，任一筛选/分页变更即重新查询。
 */
export const tiktokCreativeAssetsQueryOptions = (
  workspaceId: string,
  rawQuery: unknown,
) =>
  queryOptions({
    // rawQuery 整体作 queryKey；同 workspace 保留上一帧，避免换筛选瞬空或跨空间闪旧数据。
    placeholderData: keepPreviousDataForWorkspace(workspaceId),
    queryKey: ["workspace", workspaceId, "tiktok", "creative-assets", rawQuery],
    queryFn: () => {
      // 高级筛选 af 等条件以 JSON body 下推（接口已由 GET 改 POST，见 ADR-0056/0080）。
      // body schema 与 query schema 字段同构，但分页字段不再 z.coerce（JSON body 里
      // page/pageSize 本就是 number）；parse 后的对象直接作请求体发送。
      const body = tiktokCreativeAssetsBodySchema.parse(rawQuery);
      return apiClient.post(
        `${workspacePath(workspaceId)}/tiktok/creative-assets`,
        body,
        tiktokCreativeAssetsSuccessSchema,
      );
    },
  });

/** 指定广告账户的普通、Smart+ 与 GMV Max 系列统一运营数据。 */
export const tiktokCampaignsQueryOptions = (
  workspaceId: string,
  rawQuery: unknown,
) =>
  queryOptions({
    // rawQuery 整体作 queryKey；同 workspace 保留上一帧，避免换筛选瞬空或跨空间闪旧数据。
    placeholderData: keepPreviousDataForWorkspace(workspaceId),
    queryKey: ["workspace", workspaceId, "tiktok", "campaigns", rawQuery],
    queryFn: () => {
      // 高级筛选 af 等条件以 JSON body 下推（接口已由 GET 改 POST，见 ADR-0056）。
      // body schema 与 query schema 字段同构，但分页字段不再 z.coerce；parse 后的
      // 对象直接作请求体发送，storeId 数组在 JSON 里天然保留。
      const body = tiktokCampaignsBodySchema.parse(rawQuery);
      return apiClient.post(
        `${workspacePath(workspaceId)}/tiktok/campaigns`,
        body,
        apiSuccessSchema(tiktokCampaignsDataSchema),
      );
    },
  });

/** 单个系列在闭区间内的每日归因。日跨度最多 90 天。 */
export function fetchTikTokCampaignDetail(
  workspaceId: string,
  campaignRef: string,
  rawQuery: unknown,
  signal?: AbortSignal,
) {
  const query = tiktokCampaignDetailQuerySchema.parse(rawQuery);
  const params = new URLSearchParams({
    endDate: query.endDate,
    startDate: query.startDate,
  });
  return apiClient.get(
    `${workspacePath(workspaceId)}/tiktok/campaigns/${encodeURIComponent(campaignRef)}?${params.toString()}`,
    apiSuccessSchema(tiktokCampaignDetailDataSchema),
    { signal },
  );
}
